import { cn } from '@/lib/utils';
import { shareOfTotal, type ChartMember } from '@/lib/issuingCharts';
import { ChartEmpty } from './ChartEmpty';
import type { ToneResolver } from './chartTones';

interface MixBarProps {
  /** The whole of the period, split by category, in display order. */
  segments: readonly ChartMember[];
  tone: ToneResolver;
  formatValue: (value: number) => string;
  /**
   * Renders a share as text. Injected for the same reason `formatValue` is:
   * three locales ship, and where the sign sits and which digits are used is the
   * page's call, not this component's.
   */
  formatPercent: (share: number) => string;
  /** Names what the bar is a mix of; the legend below carries the figures. */
  ariaLabel: string;
  emptyLabel: string;
  className?: string;
}

/**
 * One horizontal bar split by share of total, with a legend that states each
 * segment's absolute figure and share.
 *
 * Accessibility: every number in the bar is also in the legend as text, so the
 * bar itself is decoration (`aria-hidden`) and the legend list — not an
 * `role="img"` wrapper, which would hide that text — is what a screen reader
 * reads.
 *
 * Segments are sized with `flex-grow` rather than a width: shares are rounded
 * individually and can sum to 99.9, and growth proportions fill the track
 * exactly where three widths of 33.3% would leave a sliver of track showing.
 */
export function MixBar({
  segments,
  tone,
  formatValue,
  formatPercent,
  ariaLabel,
  emptyLabel,
  className,
}: MixBarProps) {
  if (segments.length === 0) return <ChartEmpty label={emptyLabel} className={className} />;

  const shares = shareOfTotal(segments.map((segment) => segment.value));

  return (
    <div className={cn('min-w-0', className)}>
      <div
        aria-hidden="true"
        className="flex h-2.5 overflow-hidden rounded-full border border-line bg-paper-3"
      >
        {segments.map((segment, i) => (
          <div
            key={segment.id}
            data-testid="mix-segment"
            style={{ flexGrow: shares[i] ?? 0, flexBasis: 0, background: tone(segment.id) }}
          />
        ))}
      </div>

      <ul aria-label={ariaLabel} className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {segments.map((segment, i) => (
          <li key={segment.id} className="flex min-w-0 items-center gap-1.5 text-[11.5px]">
            <span
              aria-hidden="true"
              data-testid="mix-swatch"
              className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
              style={{ background: tone(segment.id) }}
            />
            <span className="truncate text-ink-2">{segment.label}</span>
            <span className="font-mono tabular-nums text-ink">{formatValue(segment.value)}</span>
            <span className="font-mono tabular-nums text-muted-2">
              {formatPercent(shares[i] ?? 0)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
